import { alpha, styled } from '@mui/material/styles';

interface FileUploadDropzoneRootStyleProps {
  isDragActive?: boolean;
}

const shouldForwardRootProp = (prop: PropertyKey) => prop !== 'isDragActive';

export const FileUploadDropzoneRoot = styled('button', {
  shouldForwardProp: shouldForwardRootProp,
})<FileUploadDropzoneRootStyleProps>(({ isDragActive = false, theme }) => ({
  alignItems: 'center',
  backgroundColor: isDragActive ? alpha(theme.palette.brand[100], 0.55) : theme.palette.background.paper,
  border: `${theme.spacing(0.375)} dashed ${theme.palette.brand[500]}`,
  borderRadius: theme.spacing(2),
  boxSizing: 'border-box',
  color: theme.palette.brand[500],
  cursor: 'pointer',
  display: 'grid',
  gap: theme.spacing(0.5),
  justifyItems: 'center',
  minHeight: theme.spacing(25),
  padding: theme.spacing(4),
  textAlign: 'center',
  transform: isDragActive ? 'translateY(-1px) scale(1.01)' : 'translateY(0) scale(1)',
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  width: '100%',
  '&:hover': {
    backgroundColor: alpha(theme.palette.brand[100], 0.55),
    borderColor: theme.palette.brand[600],
  },
  '&:hover [data-file-upload-dropzone-icon="true"], &:focus-visible [data-file-upload-dropzone-icon="true"]': {
    transform: 'translateY(-1px) scale(1.04)',
  },
  '&:focus-visible': {
    backgroundColor: alpha(theme.palette.brand[100], 0.55),
    boxShadow: `0 0 0 ${theme.spacing(0.375)} ${alpha(theme.palette.brand[500], 0.18)}`,
    outline: 0,
  },
  ...(isDragActive ? {
    boxShadow: `0 12px 32px ${alpha(theme.palette.brand[500], 0.18)}`,
    '& [data-file-upload-dropzone-icon="true"]': {
      transform: 'translateY(-2px) scale(1.08)',
    },
  } : {}),
  '&:disabled': {
    backgroundColor: theme.palette.neutral[50],
    borderColor: theme.palette.neutral[300],
    color: theme.palette.neutral[400],
    cursor: 'not-allowed',
    transform: 'none',
  },
}));

export const FileUploadDropzoneInput = styled('input')({
  display: 'none',
});

export const FileUploadDropzoneIconFrame = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.brand[100],
  borderRadius: '50%',
  color: theme.palette.brand[500],
  display: 'inline-flex',
  height: theme.spacing(8),
  justifyContent: 'center',
  marginBottom: theme.spacing(1),
  transition: theme.transitions.create(['background-color', 'color', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  width: theme.spacing(8),
  '& .MuiSvgIcon-root': {
    fontSize: theme.spacing(4),
  },
}));

export const FileUploadDropzoneText = styled('span')(({ theme }) => ({
  color: 'inherit',
  display: 'block',
  fontSize: theme.typography.body1.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: theme.typography.body1.lineHeight,
  maxWidth: theme.spacing(64),
  overflowWrap: 'anywhere',
}));

export const FileUploadDropzoneStrongText = styled(FileUploadDropzoneText)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
}));

export const FileUploadDropzoneHelperText = styled('span')(({ theme }) => ({
  color: 'inherit',
  display: 'grid',
  gap: theme.spacing(0.25),
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: theme.typography.caption.lineHeight,
  marginTop: theme.spacing(0.5),
}));

export const FileUploadDropzoneSelectedFile = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.brand[100], 0.55),
  border: `${theme.spacing(0.125)} solid ${theme.palette.brand[200]}`,
  borderRadius: theme.spacing(1.25),
  boxSizing: 'border-box',
  color: theme.palette.neutral[700],
  display: 'grid',
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  gap: theme.spacing(0.25),
  lineHeight: theme.typography.body2.lineHeight,
  marginTop: theme.spacing(1.5),
  maxWidth: '100%',
  minWidth: 0,
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
}));

export const FileUploadDropzoneSelectedFileName = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const FileUploadDropzoneSelectedFileMeta = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: theme.typography.caption.lineHeight,
}));
